import { useQuery } from '@tanstack/react-query';
import {
  getClosedBusinesses,
  getClosedCashAccounts,
  getClosedInvestments,
  getClosedLoans,
  getClosedNoteReceivables,
  getClosedPersonalPropertyItems,
  getClosedRealEstateProperties,
  useGraphqlClient,
} from '../../graphql';
import { ClosedAccountItem } from '../components/ClosedAccountsSection';
import { ClosedAccountType, closedAccountsQueryKey } from '../queryKeys';

export type { ClosedAccountType };

export function useClosedAccountsList(householdID: string, accountType: ClosedAccountType): {
  items: ClosedAccountItem[];
  isLoading: boolean;
} {
  const gqlClient = useGraphqlClient();

  const query = useQuery({
    enabled: !!householdID,
    queryKey: closedAccountsQueryKey(householdID, accountType),
    queryFn: () => {
      if (!householdID) return [];
      switch (accountType) {
        case 'CashAccount':
          return getClosedCashAccounts(gqlClient, householdID);
        case 'Investment':
          return getClosedInvestments(gqlClient, householdID);
        case 'Business':
          return getClosedBusinesses(gqlClient, householdID);
        case 'RealEstate':
          return getClosedRealEstateProperties(gqlClient, householdID);
        case 'Loan':
          return getClosedLoans(gqlClient, householdID);
        case 'PersonalPropertyItem':
          return getClosedPersonalPropertyItems(gqlClient, householdID);
        case 'NoteReceivable':
          return getClosedNoteReceivables(gqlClient, householdID);
        default:
          throw new Error(`Unhandled closed account type: ${accountType as string}`);
      }
    },
  });

  const items = (query.data ?? [])
    .filter((item) => item.closedAt != null)
    .map((item) => ({ ...item, closedAt: item.closedAt ?? undefined })) as ClosedAccountItem[];
  return { items, isLoading: query.isFetching };
}
